const SCALAR_FIELDS = new Set([
  "name",
  "description",
  "control_type",
  "cycle",
  "process",
  "control_owner",
  "configured_state",
  "is_template",
  "duplicated_from",
]);

const SECTION_FIELDS = new Set([
  "attributes",
  "key_report_items",
  "procedures",
  "custom_fields",
]);

const SKIP_FIELDS = new Set([
  "id",
  "created_at",
  "created_by",
  "updated_at",
  "workspace_id",
  "ipe_procedure_id",
  "attribute_lookup",
  "column_lookup",
  "column_name_lookup",
  "ordered_procedures",
]);

interface ChangeDiffEntry {
  old: unknown;
  new: unknown;
}

export function isChangeDiffEntry(value: unknown): value is ChangeDiffEntry {
  if (typeof value !== "object" || value === null) return false;
  return "old" in value && "new" in value;
}

export interface ScalarChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface SectionChange {
  field: string;
  oldItems: Array<Record<string, unknown>>;
  newItems: Array<Record<string, unknown>>;
}

export interface ParsedControlDiff {
  scalarChanges: ScalarChange[];
  sectionChanges: SectionChange[];
}

function normalizeToArray(value: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is Record<string, unknown> =>
        typeof item === "object" && item !== null,
    );
  }
  if (typeof value === "object" && value !== null) {
    return Object.values(value).filter(
      (item): item is Record<string, unknown> =>
        typeof item === "object" && item !== null,
    );
  }
  return [];
}

export function parseControlDiff(
  changeDiff: Record<string, unknown>,
): ParsedControlDiff {
  const scalarChanges: ScalarChange[] = [];
  const sectionChanges: SectionChange[] = [];

  for (const key of Object.keys(changeDiff)) {
    if (SKIP_FIELDS.has(key)) continue;

    const entry = changeDiff[key];
    if (!isChangeDiffEntry(entry)) continue;

    if (SECTION_FIELDS.has(key)) {
      sectionChanges.push({
        field: key,
        oldItems: normalizeToArray(entry.old),
        newItems: normalizeToArray(entry.new),
      });
    } else if (SCALAR_FIELDS.has(key)) {
      scalarChanges.push({
        field: key,
        oldValue: entry.old,
        newValue: entry.new,
      });
    }
  }

  return { scalarChanges, sectionChanges };
}

const ITEM_SKIP_FIELDS = new Set([
  "id",
  "created_at",
  "created_by",
  "updated_at",
  "workspace_id",
  "control_id",
  "ipe_procedure_id",
  "attribute_order",
  "attributeOrder",
  "procedure_order",
  "procedureOrder",
]);

export interface ArrayDiffItem {
  label: string;
  kind: "added" | "removed" | "modified";
  item: Record<string, unknown>;
  oldItem?: Record<string, unknown>;
  changedKeys?: string[];
}

function getLabel(item: Record<string, unknown>, matchKey: string): string {
  const val = item[matchKey];
  if (typeof val === "string" && val.length > 0) return val;
  return "";
}

export const PROCEDURE_ALLOWED_FIELDS = new Set([
  "name",
  "description",
  "columns",
]);

export const COLUMN_ALLOWED_FIELDS = new Set([
  "name",
  "description",
  "pass_criteria",
  "fail_criteria",
  // Analytics columns are authored with the same criteria set as attribute tests,
  // plus the workflow they delegate the verdict to. The workflow *binding* is not
  // listed: it changes only via the attach and configure routes, never via this
  // authoring form.
  // The next four entries also exist on attribute-test columns; before they were
  // listed, an edit to one flagged the columns list as changed while rendering no
  // detail.
  "na_criteria",
  "procedure_guidance",
  "tolerances",
  "expected_evidences",
  "workflow_name",
  "drafted_test",
]);

const COLUMN_NESTED_KEYS = [
  "support_column",
  "evaluated_column",
  "attribute_test_column",
  "analytics_column",
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function flattenColumn(
  col: Record<string, unknown>,
): Record<string, unknown> {
  const flat: Record<string, unknown> = {};

  for (const key of COLUMN_ALLOWED_FIELDS) {
    if (col[key] !== undefined) {
      flat[key] = col[key];
    }
  }

  if (flat["name"] === undefined) {
    for (const nestedKey of COLUMN_NESTED_KEYS) {
      const nested = col[nestedKey];
      if (isRecord(nested)) {
        for (const key of COLUMN_ALLOWED_FIELDS) {
          if (flat[key] === undefined && nested[key] !== undefined) {
            flat[key] = nested[key];
          }
        }
      }
    }
  }

  return flat;
}

export function flattenColumns(
  columns: Array<Record<string, unknown>>,
): Array<Record<string, unknown>> {
  return columns.map(flattenColumn);
}

export function diffArrayByLabel(
  oldArr: Array<Record<string, unknown>>,
  newArr: Array<Record<string, unknown>>,
  matchKey: string,
  allowedKeys?: Set<string>,
): ArrayDiffItem[] {
  const oldByLabel = new Map<string, Array<Record<string, unknown>>>();
  for (const item of oldArr) {
    const label = getLabel(item, matchKey);
    if (!label) continue;
    const existing = oldByLabel.get(label) ?? [];
    existing.push(item);
    oldByLabel.set(label, existing);
  }

  const newByLabel = new Map<string, Array<Record<string, unknown>>>();
  for (const item of newArr) {
    const label = getLabel(item, matchKey);
    if (!label) continue;
    const existing = newByLabel.get(label) ?? [];
    existing.push(item);
    newByLabel.set(label, existing);
  }

  const results: ArrayDiffItem[] = [];

  for (const [label, newItems] of newByLabel) {
    const oldItems = oldByLabel.get(label) ?? [];
    const matchCount = Math.min(oldItems.length, newItems.length);

    for (let i = 0; i < matchCount; i++) {
      const oldItem = oldItems[i];
      const newItem = newItems[i];
      const changedKeys: string[] = [];
      const allKeys = new Set([
        ...Object.keys(oldItem),
        ...Object.keys(newItem),
      ]);
      for (const k of allKeys) {
        if (k === matchKey) continue;
        if (allowedKeys ? !allowedKeys.has(k) : ITEM_SKIP_FIELDS.has(k))
          continue;
        if (JSON.stringify(oldItem[k]) !== JSON.stringify(newItem[k])) {
          changedKeys.push(k);
        }
      }
      if (changedKeys.length > 0) {
        results.push({
          label,
          kind: "modified",
          item: newItem,
          oldItem,
          changedKeys,
        });
      }
    }

    for (let i = matchCount; i < newItems.length; i++) {
      results.push({ label, kind: "added", item: newItems[i] });
    }
  }

  for (const [label, oldItems] of oldByLabel) {
    const newItems = newByLabel.get(label) ?? [];
    for (let i = newItems.length; i < oldItems.length; i++) {
      results.push({ label, kind: "removed", item: oldItems[i] });
    }
  }

  return results;
}

export function isControlEntity(entityType: string): boolean {
  return entityType === "control";
}
